import { axiosIns, anotherAxiosIns } from './index';
import { ApiResponse, ListApiResponse } from '../typings/ApiResponse';
import {
  AddNewResidentTypes,
  ResidentDetails,
  Conversation,
  LastedUpdateDateData,
} from '../typings/ResidentTypes';
import {
  AcpUpdateData,
  SharingACPDetails,
  SharingBody,
} from '../typings/ACPTypes';

const residentURL = '/carehome/resident';

const addNewResident = async (
  residentData: AddNewResidentTypes
): Promise<ApiResponse<ResidentDetails>> => {
  const response = await axiosIns.post(residentURL, residentData);
  return response.data;
};

const editResidentStepOne = async (
  slug: string,
  residentData: AddNewResidentTypes
): Promise<ApiResponse<ResidentDetails>> => {
  const response = await axiosIns.put(
    `/carehome/resident/${slug}`,
    residentData
  );
  return response.data;
};

const submitResidentThreeSteps = async (data: any) => {
  const response = await anotherAxiosIns.post('/document/advance-v1', data);
  return response.data;
};

const getResidentList = async (
  partnerCarehomeId: string,
  page: number,
  limit: number,
  full_name: string
) => {
  const response = await axiosIns.get(
    `/carehome/residents/${partnerCarehomeId}`,
    {
      params: {
        page,
        limit,
        full_name,
      },
    }
  );
  return response.data;
};

const getThreeStepsDetails = async (user_id: string) => {
  const response = await anotherAxiosIns.post('document/advance/all-v1', {
    user_id,
  });

  return response.data;
};

const downloadACPdocument = async (user_id: string, slug: string) => {
  const response = await anotherAxiosIns.get(
    `document/advance/download-v1/${user_id}`,
    {
      params: {
        slug,
      },
      responseType: 'blob',
    }
  );

  return response.data;
};

const getStepOneDetails = async (
  id: string
): Promise<ApiResponse<ResidentDetails>> => {
  const response = await axiosIns.get(`${residentURL}/${id}`);
  return response.data;
};

const shareACP = async (
  data: SharingBody
): Promise<ApiResponse<SharingACPDetails>> => {
  const response = await axiosIns.post(`${residentURL}/recipient/share`, data);
  return response.data;
};

const getACPRecipientList = async (
  userId: string,
  page: number,
  limit: number,
  type: string
) => {
  const response = await axiosIns.get(`${residentURL}/recipients/${userId}`, {
    params: {
      page,
      limit,
      type,
    },
  });
  return response.data;
};

const markCompleteACP = async (userId: string) => {
  const response = await axiosIns.post(`${residentURL}/mark-completed`, {
    carehome_user_id: userId,
  });
  return response.data;
};

const updateResidentStatus = async (
  slug: string,
  type: string,
  value?: string
) => {
  let response;
  const url = `${residentURL}/status/${slug}?type=${type}`;

  if (type === 'mental_capacity' || type === 'prev_self_completed_ACP') {
    response = await axiosIns.put(url, {
      value,
    });
  } else {
    response = await axiosIns.put(url);
  }

  return response.data;
};

const changeCareStatus = async (
  slug: string,
  careStatus: string,
  anotherValue?: string
) => {
  const response = await axiosIns.post(`${residentURL}/mark-change/${slug}`, {
    careStatus,
    value: anotherValue,
  });
  return response.data;
};

const sendConversation = async (props: {
  fullName: string;
  email?: string;
  phoneNumber?: string;
  relationship?: string;
  isAllowed?: boolean | null;
  nextOfKin: boolean;
  slug: string;
}): Promise<ApiResponse<Conversation>> => {
  const {
    fullName,
    email,
    phoneNumber,
    relationship,
    isAllowed,
    nextOfKin,
    slug,
  } = props;
  const response = await axiosIns.post(`${residentURL}/conversation`, {
    fullName,
    email,
    phoneNumber,
    relationship,
    isAllowed,
    nextOfKin,
    slug,
  });
  return response.data;
};

const getConversationList = async (
  page: number,
  limit: number,
  slug: string
): Promise<ApiResponse<ListApiResponse<Conversation>>> => {
  const response = await axiosIns.get(`${residentURL}/conversation/${slug}`, {
    params: {
      page,
      limit,
    },
  });
  return response.data;
};

const addNextOfKin = async (
  slug: string,
  name: string,
  relationship: string
) => {
  const response = await axiosIns.post(`${residentURL}/next-of-kin/${slug}`, {
    name,
    relationship,
  });
  return response.data;
};

const addPrognosis = async (slug: string, prognosis: string) => {
  const response = await axiosIns.post(`${residentURL}/prognosis/${slug}`, {
    prognosis,
  });
  return response.data;
};

const getLogs = async (
  userId: string,
  page: number,
  limit: number
): Promise<ApiResponse<ListApiResponse<LastedUpdateDateData>>> => {
  const response = await axiosIns.get(`carehome/team/logs/${userId}`, {
    params: {
      page,
      limit,
    },
  });
  return response.data;
};

const getAcpLogs = async (
  partnerId: string,
  page: number,
  limit: number
): Promise<ApiResponse<ListApiResponse<AcpUpdateData>>> => {
  const response = await axiosIns.get(
    `carehome/team/logs-by-partner/${partnerId}`,
    {
      params: {
        page,
        limit,
      },
    }
  );
  return response.data;
};

export {
  addNewResident,
  getResidentList,
  getStepOneDetails,
  editResidentStepOne,
  submitResidentThreeSteps,
  getThreeStepsDetails,
  shareACP,
  getACPRecipientList,
  markCompleteACP,
  updateResidentStatus,
  changeCareStatus,
  sendConversation,
  getConversationList,
  addNextOfKin,
  addPrognosis,
  getLogs,
  getAcpLogs,
  downloadACPdocument,
};
